import { type User } from '@kalos/kalos-rpc';
import { Button, Toaster } from '@kalos/ui';
import { useGoogleLogin } from '@react-oauth/google';
import { Navigate, useLocation, useRouter } from '@tanstack/react-router';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { useKalosLogoQuery } from '../hooks/react-query/useKalosLogoQuery';
import { useAppBarHeight } from '../hooks/useAppBarHeight';
import { ErrorBoundary } from '../modules/ComponentsLibrary/ErrorBoundary';
import { StyledPage } from '../modules/PageWrapper/styled';
import SideMenu from '../modules/SideMenu/main';
// import { Softphone } from '../modules/Softphone';
// import { SoftPhoneStoreProvider } from '../modules/Softphone/context/SoftPhoneStoreProvider';
import { authFlow, authScope } from './constants';

export const RequireAuth = ({
  children,
  requiredPermissions,
  userCheck,
}: {
  children: JSX.Element;
  requiredPermissions?: number[];
  userCheck?: (user: User) => boolean;
}) => {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.authChecked) {
    return null; // or some loading spinner
  }

  if (auth.user.id === 0) {
    return (
      <Navigate
        to="/"
        search={{
          from: location.pathname,
        }}
        replace
      />
    );
  }

  if (userCheck) {
    const isAllowed = userCheck(auth.user);
    if (!isAllowed) {
      return "You don't have permission to view this page";
    }
  }

  if (requiredPermissions) {
    const userPermissions = auth.user.permissionGroups.map((p) => p.id);
    const hasAllPermissions = requiredPermissions.every((perm) => userPermissions.includes(perm));

    if (!hasAllPermissions) {
      return null;
    }
  }

  return children;
};

// Layout component that includes SideMenu, Softphone, and wraps content
export const Layout = ({ children }: { children: ReactNode }) => {
  const appBar = useRef<HTMLDivElement>(null);
  useAppBarHeight(appBar);
  return (
    <ErrorBoundary>
      {/* <SoftPhoneStoreProvider> */}
      <StyledPage>
        <div className="PageWrapper">
          <div ref={appBar} className="PageWrapperMenu primary-bg">
            <SideMenu />
          </div>
          <div className="PageWrapperContent">
            <div className="spacer"></div>
            {children}
          </div>
        </div>
      </StyledPage>
      {/* <Softphone /> */}
      <Toaster underAppBar />
      {/* </SoftPhoneStoreProvider> */}
    </ErrorBoundary>
  );
};

// Basic login page that doesn't use router hooks
export const SelectLoginPage = () => {
  const auth = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const kalosLogo = useKalosLogoQuery();

  // Handle redirect after successful login
  useEffect(() => {
    if (shouldRedirect && auth.user.id !== 0) {
      window.location.href = '/home';
    }
  }, [shouldRedirect, auth.user.id]);

  // Simple function to handle login success
  const handleLoginSuccess = async (response: any) => {
    if (!response || !response.code) {
      setError('No authorization code received');
      return;
    }

    setIsLoggingIn(true);
    setError(null);

    try {
      // Simple signin without navigation callback
      await auth.signin(response.code);
      // Set flag to trigger redirect in the effect
      setShouldRedirect(true);
    } catch (err) {
      console.error('Login failed:', err);
      setError('Login failed. Please try again.');
      setIsLoggingIn(false);
    }
  };

  // Configure Google login
  const login = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: (error) => {
      console.error('Google login error:', error);
      setError('Google login failed. Please try again.');
    },
    flow: authFlow,
    redirect_uri: window.location.origin,
    scope: authScope,
  });

  return (
    <main className="bg-background flex min-h-[80svh] flex-col items-center justify-center px-4 py-10">
      <div className="bg-card w-full max-w-sm rounded-lg border p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-center gap-6 text-center">
          {kalosLogo.data ? (
            <img src={kalosLogo.data} alt="Kalos Services" className="h-12 w-auto" />
          ) : (
            // Holds the logo's space while it loads, so the card doesn't jump.
            <div className="h-12" aria-hidden />
          )}
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold">Sign in to Kalos</h1>
            <p className="text-muted-foreground text-sm">Use your Kalos Google account.</p>
          </div>
        </div>

        {error && (
          <p role="alert" className="bg-danger text-danger-foreground mt-6 rounded-md p-3 text-sm">
            {error}
          </p>
        )}

        <Button
          className="mt-6 w-full"
          type="button"
          onClick={() => login()}
          disabled={isLoggingIn}
          isLoading={isLoggingIn}
        >
          {isLoggingIn ? 'Signing in…' : 'Employee sign in'}
        </Button>

        <p className="text-muted-foreground mt-4 text-center text-sm">
          Can&apos;t sign in? Contact Webtech for help.
        </p>
      </div>
    </main>
  );
};

export const Logout = () => {
  const auth = useAuth();
  const router = useRouter();
  const hasLoggedOut = React.useRef(false);

  // Use useEffect to perform the logout operation after the component mounts
  // This ensures we're inside the RouterProvider context
  useEffect(() => {
    // Prevent duplicate logout attempts that can cause infinite loops
    if (!hasLoggedOut.current) {
      hasLoggedOut.current = true;
      auth.signout(() => {
        router.navigate({ to: '/' });
        router.invalidate();
      });
    }
  }, [auth, router]);

  return null;
};
